import { Client, ClientsSummary, ClientsSummaryComparison } from '../store/clients/interfaces';
import { Transaction } from '../store/transactions/interfaces';

const SUMMARY_PERIOD_DAYS = 30;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const getClientUniqueId = (client: Client) => {
  const id = client.merchantClientId || client.clientId || String(client.id);
  return id ? String(id).trim() : '';
};

const getDistinctClientIds = (clients: Client[]) => {
  const uniqueIds = new Set<string>();

  clients.forEach(client => {
    const uniqueId = getClientUniqueId(client);
    if (uniqueId) {
      uniqueIds.add(uniqueId);
    }
  });

  return uniqueIds;
};

const parseDate = (value?: string) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getStartOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const isWithinLastDays = (date: Date | null, days: number, now = new Date()) => {
  if (!date) {
    return false;
  }

  const periodStart = getStartOfDay(new Date(now.getTime() - days * MS_PER_DAY));
  return date >= periodStart;
};

const isWithinPreviousDaysWindow = (date: Date | null, days: number, now = new Date()) => {
  if (!date) {
    return false;
  }

  const currentPeriodStart = getStartOfDay(new Date(now.getTime() - days * MS_PER_DAY));
  const previousPeriodStart = getStartOfDay(new Date(now.getTime() - days * 2 * MS_PER_DAY));
  return date >= previousPeriodStart && date < currentPeriodStart;
};

const calcDiffPercentage = (current: number, previous: number) => {
  if (previous === 0) {
    return current > 0 ? 100 : 0;
  }

  return ((current - previous) / previous) * 100;
};

const buildComparison = (current: number, previous: number): ClientsSummaryComparison => {
  const diffPercentage = calcDiffPercentage(current, previous);

  return {
    current,
    previous,
    diffPercentage,
    type: current > previous ? 'up' : current < previous ? 'down' : 'equal',
  };
};

const aggregateTransactionsByPeriod = (transactions: Transaction[], now = new Date()) => {
  const last30 = {
    volume: 0,
    clientIds: new Set<string>(),
  };
  const previous30 = {
    volume: 0,
    clientIds: new Set<string>(),
  };

  transactions.forEach(transaction => {
    if (transaction.status !== 'COMPLETED') {
      return;
    }

    const date = parseDate(transaction.creationDate);
    const amount = Number(transaction.balanceAmount) || 0;
    const clientId = transaction.clientId ? String(transaction.clientId).trim() : '';

    if (isWithinLastDays(date, SUMMARY_PERIOD_DAYS, now)) {
      last30.volume += amount;
      if (clientId) {
        last30.clientIds.add(clientId);
      }
      return;
    }

    if (isWithinPreviousDaysWindow(date, SUMMARY_PERIOD_DAYS, now)) {
      previous30.volume += amount;
      if (clientId) {
        previous30.clientIds.add(clientId);
      }
    }
  });

  return { last30, previous30 };
};

export const formatSummaryDateParam = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getClientsSummaryDateRange = (now = new Date()) => {
  const to = formatSummaryDateParam(now);
  const fromDate = new Date(now.getTime() - SUMMARY_PERIOD_DAYS * 2 * MS_PER_DAY);
  const from = formatSummaryDateParam(fromDate);

  return { from, to };
};

export const computeClientsSummary = (clients: Client[], transactions: Transaction[] = []): ClientsSummary => {
  const totalClients = getDistinctClientIds(clients).size;

  const { last30, previous30 } = aggregateTransactionsByPeriod(transactions);
  const activeClients = last30.clientIds.size;

  const transactedVolume = clients.reduce(
    (sum, client) => sum + (Number(client.transactedAmount) || 0),
    0
  );

  const averageClientValue = totalClients ? transactedVolume / totalClients : 0;

  const averageLast30 = last30.clientIds.size ? last30.volume / last30.clientIds.size : 0;
  const averagePrevious30 = previous30.clientIds.size
    ? previous30.volume / previous30.clientIds.size
    : 0;

  const currencyClient = clients.find(client => client.transactedCurrency || client.currency);
  const currencyTransaction = transactions.find(transaction => transaction.balanceCurrency);
  const currency =
    currencyClient?.transactedCurrency || currencyClient?.currency || currencyTransaction?.balanceCurrency || '';

  return {
    totalClients,
    activeClients,
    transactedVolume,
    averageClientValue,
    currency,
    transactedVolumeComparison: buildComparison(last30.volume, previous30.volume),
    averageClientValueComparison: buildComparison(averageLast30, averagePrevious30),
  };
};
